import styled from '@emotion/styled/macro';
import { BaseInput, BaseSelect, Variables } from '../../base';

export const OptionItem = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
    padding: 16px 0;

    &[data-layout='stack'] {
        flex-direction: column;
        gap: 12px;
    }

    &:not(:last-of-type) {
        border-bottom: 1px solid ${Variables.Colors.n15_48};
    }
`;

export const OptionContent = styled.div`
    flex: 1;
    min-width: 0;
`;

export const OptionTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
`;

export const OptionLabel = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${Variables.Colors.p500};
    line-height: 1.3;
`;

export const OptionKey = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    color: ${Variables.Colors.n70};
    background: ${Variables.Colors.n15_24};
    padding: 1px 7px;
    border-radius: 4px;
`;

export const OptionDescription = styled.div`
    font-size: 14px;
    color: ${Variables.Colors.n70};
    line-height: 1.5;
    margin-top: 4px;
`;

export const OptionAllowed = styled.div`
    font-size: 12px;
    color: ${Variables.Colors.n70};
    margin-top: 6px;
`;

export const OptionAllowedLabel = styled.span`
    font-weight: 500;
`;

export const OptionControl = styled.div`
    flex: none;
    padding-top: 2px;

    &[data-layout='stack'] {
        width: 100%;
        max-width: 520px;
        padding-top: 0;
    }

    &[data-readonly='true'] {
        pointer-events: none;
        opacity: 0.92;
    }
`;

export const SegmentedControl = styled.div`
    display: inline-flex;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 6px;
    overflow: hidden;

    &[aria-disabled='true'] {
        pointer-events: none;
        opacity: 0.92;
    }
`;

export const SegmentButton = styled.button<{ active?: boolean; variant?: 'neutral' | 'primary' }>`
    padding: 6px 16px;
    border: none;
    border-left: 1px solid ${Variables.Colors.n15_48};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    cursor: pointer;
    white-space: nowrap;

    &:first-of-type {
        border-left: none;
    }

    ${({ variant, active }) =>
        variant === 'neutral'
            ? `
        background: ${active ? Variables.Colors.n70 : Variables.Colors.white};
        color: ${active ? Variables.Colors.white : Variables.Colors.n70};
    `
            : `
        background: ${active ? Variables.ThemeLight.PrimaryConstant6 : Variables.Colors.white};
        color: ${active ? Variables.Colors.white : Variables.Colors.n70};
    `}

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

export const SegmentButtonUnset = styled(SegmentButton)`
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    background: ${({ active }) => (active ? Variables.Colors.n15_24 : Variables.Colors.white)};
    color: ${({ active }) => (active ? Variables.Colors.n70 : Variables.Colors.n50)};
`;

export const EnumSelect: any = styled(BaseSelect)`
    min-width: 130px;
    .select__control {
        min-height: 34px;
        border: 1px solid ${Variables.Colors.n15_48};
        border-radius: 6px;
        background: ${Variables.Colors.white};
        box-shadow: none;
    }

    .select__control--is-focused {
        border: 1px solid ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 2px ${Variables.ThemeLight.PrimaryMild2};
    }

    .select__value-container {
        margin: 0;
        padding: 0 8px;
        font-size: 14px;
        color: ${Variables.Colors.p500};
    }

    .select__single-value {
        color: ${Variables.Colors.p500};
    }

    .select__indicator {
        padding: 0 7px;
    }

    .select__control--is-disabled {
        cursor: not-allowed;
        background: ${Variables.ThemeLight.Background1};
        color: ${Variables.Colors.n70};
        opacity: 0.92;
    }
`;

export const TextInput: any = styled(BaseInput)`
    min-width: 180px;
    height: auto;
    padding: 7px 10px;
    border: 1px solid ${Variables.Colors.n15_48};
    border-radius: 6px;
    background: ${Variables.Colors.white};
    font-size: 14px;
    color: ${Variables.Colors.p500};
    outline: none;

    &:focus {
        border-color: ${Variables.ThemeLight.PrimaryConstant6};
        box-shadow: 0 0 0 2px ${Variables.ThemeLight.PrimaryMild2};
    }

    &::placeholder {
        color: ${Variables.Colors.n50};
    }

    &:disabled,
    &:read-only {
        cursor: not-allowed;
        background: ${Variables.ThemeLight.Background1};
        color: ${Variables.Colors.n70};
        opacity: 0.92;
    }
`;
